export type SkillPracticeTaskStatus = 'CREATED' | 'IN_WORK' | 'COMPLETED' | 'CANCELLED';
export type AiChatRole = 'user' | 'assistant';
export type UserRole = 'ROLE_HEAD' | 'ROLE_ADMIN' | 'ROLE_ADMIN_SPECIALIZATION' | 'ROLE_USER';
export type SkillAiOperation = 'CHAT' | 'TEST_GENERATION';

export interface SkillSpecializationCreateRequest {
  name: string;
  description: string;
}

export interface CuratorAssignRequest {
  curatorIds: string[];
}

export interface TheoryLevelRequest {
  topics: string[];
  questionsCount: number;
  timeLimitMinutes: number;
  passingPercentage: number;
}

export interface PracticeLevelRequest {
  description: string;
}

export interface AiChatSendRequest {
  message: string;
}

export interface SkillTestSubmitRequest {
  answers: { [questionIndex: string]: number };
}

export interface SkillLearningGeneralSettingsResponse {
  testRetakeCooldownHours: number;
  testGenerationLimitPerUserPerDay: number;
  maxChatMessageLength: number;
  chatMessagesPerUserPerDay: number;
  chatMessagesPerUserPerMinute: number;
}

export interface TheoryLevelResponse {
  topics: string[];
  documentFileId: string | null;
  document: SkillDocumentResponse | null;
  questionsCount: number;
  timeLimitMinutes: number;
  passingPercentage: number;
}

export interface PracticeLevelResponse {
  description: string;
  documentFileId: string | null;
  document: SkillDocumentResponse | null;
}

export interface SkillSpecializationResponse {
  id: string;
  name: string;
  description: string;
  groupId: string;
  curatorIds: string[];
  theoryLevel: TheoryLevelResponse;
  practiceLevel2: PracticeLevelResponse;
  practiceLevel3: PracticeLevelResponse;
  active: boolean;
}

export interface SkillSpecializationListItem {
  id: string;
  name: string;
  description: string;
  groupId: string;
  active: boolean;
}

export interface SkillProgressResponse {
  id: string;
  userId: string;
  specializationId: string;
  currentLevel: number;
  topicChecklist: { [topicIndex: string]: boolean };
  testPassedAt: string | null;
  level2TaskStatus: SkillPracticeTaskStatus | null;
  level3TaskStatus: SkillPracticeTaskStatus | null;
}

export interface SkillTrainingProgressResponse {
  specializationId: string;
  specializationName: string;
  completedLevels: number;
  totalLevels: number;
  completed: boolean;
}

export interface SkillDepartmentProgressResponse {
  groupId: string;
  groupName: string;
  completedTrainings: number;
  totalTrainings: number;
  trainings: SkillTrainingProgressResponse[];
}

export interface SkillDepartmentLearningProgressResponse {
  totalCompletedTrainings: number;
  totalTrainings: number;
  departments: SkillDepartmentProgressResponse[];
}

export interface SkillWeightSumResponse {
  sumLevel: number;
  sumWeight: number;
}

export interface AiChatMessage {
  role: AiChatRole;
  content: string;
}

export interface AiChatMessageResponse {
  response: string;
}

export interface SkillTestQuestion {
  questionText: string;
  answers: string[];
}

export interface SkillTestGenerateResponse {
  questions: SkillTestQuestion[];
  timeLimitMinutes: number;
}

export interface SkillTestResultResponse {
  passed: boolean;
  correctCount: number;
  totalCount: number;
  percentage: number;
}

export interface SkillPracticeTaskResponse {
  id: string;
  status: SkillPracticeTaskStatus;
  assignedCuratorId: string | null;
  assignedCuratorProfileLink: string | null;
  assignedCurator: SkillAssignedCuratorResponse | null;
  planfixTaskId: number | null;
  planfixTaskUrl: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface SkillWeightSumParams {
  from: string;
  to: string;
}

export interface SkillAiDateRangeParams {
  from: string;
  to: string;
}

export interface SkillAiOperationBreakdownItem {
  operation: SkillAiOperation;
  requestCount: number;
  successCount: number;
  failureCount: number;
  successRate: number;
}

export interface SkillAiModelBreakdownItem {
  provider: string;
  model: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface SkillAiStatisticsSummaryResponse {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  estimatedCost: number;
  averageLatencyMs: number;
  successRate: number;
  operationBreakdown: SkillAiOperationBreakdownItem[];
  modelBreakdown: SkillAiModelBreakdownItem[];
}

export interface SkillAiTimeseriesItem {
  date: string;
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface SkillAiTopUserItem {
  userId: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
  successRate: number;
}

export interface SkillAiTopSpecializationItem {
  specializationId: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
  uniqueUsers: number;
}

export interface SkillAiLogItem {
  id: string;
  createdAt: string;
  userId: string;
  specializationId: string;
  operation: SkillAiOperation;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  success: boolean;
  errorCode: string | null;
  latencyMs: number;
  requestedQuestionsCount: number | null;
  actualQuestionsCount: number | null;
}

export interface SkillAiLogsResponse {
  items: SkillAiLogItem[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface SkillAiLogsQueryParams {
  from?: string;
  to?: string;
  page: number;
  size: number;
  operation?: SkillAiOperation | null;
  success?: boolean | null;
  model?: string;
  userId?: string;
  specializationId?: string;
}

export interface SkillAiBudgetTopUserItem {
  userId: string;
  tokensUsed: number;
  chatMessagesToday: number;
  testsGeneratedToday: number;
  status: string;
}

export interface SkillAiBudgetStatusResponse {
  globalTokensUsed: number;
  globalTokenLimit: number;
  usagePercentage: number;
  status: string;
  testGenerationRequestsToday: number;
  topUsers: SkillAiBudgetTopUserItem[];
}

export interface SkillAiBudgetTokensApiResponse {
  used: number;
  limit: number;
  percent: number;
  status: string;
}

export interface SkillAiBudgetStatusApiResponse {
  globalTokens?: SkillAiBudgetTokensApiResponse;
  globalStatus?: string;
  globalLimit?: number;
  testGenerationRequestsToday?: number;
  topUsers?: SkillAiBudgetTopUserItem[];
}

export interface SkillGroup {
  id: string;
  name: string;
}

export interface SkillAvailableCurator {
  id: string;
  firstName: string;
  lastName: string;
}

export interface SkillAssignedCuratorResponse {
  id: string;
  fullName: string;
  profileLink: string;
  phone: string | null;
}

export interface SkillDocumentResponse {
  fileId: string;
  fileName: string;
  fileExtension: string;
  size: number;
  contentType: string;
}

export type SkillRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SkillRequestCreateRequest {
  specializationId: string;
}

export interface SkillRequestResponse {
  id: string;
  userId: string;
  specializationId: string;
  status: SkillRequestStatus;
  createdAt: string;
  resolvedAt: string | null;
}

export function createDefaultSkillLearningGeneralSettings(): SkillLearningGeneralSettingsResponse {
  return {
    testRetakeCooldownHours: 10,
    testGenerationLimitPerUserPerDay: 2,
    maxChatMessageLength: 2000,
    chatMessagesPerUserPerDay: 50,
    chatMessagesPerUserPerMinute: 10,
  };
}

export function mergeSkillLearningGeneralSettings(
  settings?: Partial<SkillLearningGeneralSettingsResponse> | null,
): SkillLearningGeneralSettingsResponse {
  return Object.assign({}, createDefaultSkillLearningGeneralSettings(), settings || {});
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/models/skill-learning.model.ts